import { LoaderCircle, type LucideIcon, VolumeOff, VolumeX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { CARD, PILL_TONE, TEXT_CAPTION } from '../recipes'
import { AudioScrubber } from './AudioScrubber'
import type { PlaybackState } from './questioning-types'
import type { RecordingAudioState } from './recording-types'

export interface RecordingPlayerProps {
  /** Whether there is audio to play: none yet, being fetched, ready, or not playable here. */
  audio: RecordingAudioState
  playback: PlaybackState
  /** Where the speech is, 0..1 per bar (`waveformFromTurns`). */
  waveform: readonly number[]
  actions: { onTogglePlay: () => void; onSeek: (ms: number) => void }
}

/** No recording has a bookmark in the light version. */
const NO_BOOKMARKS = [] as const

interface NoteLook {
  Icon: LucideIcon
  spin?: boolean
  warning?: boolean
}

const NOTE: Record<Exclude<RecordingAudioState, 'ready'>, NoteLook> = {
  none: { Icon: VolumeOff },
  loading: { Icon: LoaderCircle, spin: true },
  unavailable: { Icon: VolumeX, warning: true },
}

/** In place of the player: why there is nothing to play, in words. */
function PlayerNote({ state }: { state: Exclude<RecordingAudioState, 'ready'> }) {
  const { t } = useTranslation()
  const { Icon, spin, warning } = NOTE[state]
  return (
    <p
      role={warning ? 'alert' : 'status'}
      className={cn(
        CARD,
        TEXT_CAPTION,
        'flex min-h-18 items-center gap-3 p-4 font-medium @5xl/humint:min-h-16',
        warning ? PILL_TONE.warning : 'text-muted-foreground'
      )}
    >
      <Icon
        aria-hidden
        className={cn('size-5 shrink-0', spin && 'animate-spin motion-reduce:animate-none')}
      />
      {t(`humint.questioning.light.page.player.${state}`)}
    </p>
  )
}

/**
 * The recording's player (UC-8, light): the scrubber over a waveform of where the speech is,
 * once the audio is here. Until then, and when this browser cannot play it, a line says so in
 * its place — the transcript stays readable either way.
 */
export function RecordingPlayer({ audio, playback, waveform, actions }: RecordingPlayerProps) {
  if (audio !== 'ready') return <PlayerNote state={audio} />
  return (
    <AudioScrubber
      playback={playback}
      waveform={waveform}
      bookmarks={NO_BOOKMARKS}
      actions={actions}
    />
  )
}
